import { formatLocalTimeOfDay as formatTimeOfDay } from '@fundline/shared/compliance/display';
import { parseTimeOfDay } from '@fundline/shared/compliance/window';
import type { OverrideInput, OverrideRecord } from './use-sending-rules.ts';

/**
 * The workspace-rule form on Settings → Sending hours (M7): its fields, its checks and how a rule reads.
 * Pure, so it is unit-tested; the database checks the same limits again (constraints, RLS).
 */

export function describeOverride(
  o: Pick<
    OverrideRecord,
    'allowedStart' | 'allowedEnd' | 'sundayAllowed' | 'maxPer24h' | 'capAppliesTo'
  >,
): string {
  const parts: string[] = [];
  const start = parseTimeOfDay(o.allowedStart);
  const end = parseTimeOfDay(o.allowedEnd);
  if (start !== null && end !== null)
    parts.push(`${formatTimeOfDay(start)}–${formatTimeOfDay(end)}`);
  else if (start !== null) parts.push(`from ${formatTimeOfDay(start)}`);
  else if (end !== null) parts.push(`until ${formatTimeOfDay(end)}`);
  if (!o.sundayAllowed) parts.push('no Sundays');
  if (o.maxPer24h !== null) {
    parts.push(
      `${o.maxPer24h} texts per 24 hours${o.capAppliesTo.length === 1 && o.capAppliesTo[0] === 'campaign' ? ' (campaigns and sequences)' : ''}`,
    );
  }
  return parts.join(' · ');
}

export interface FormState {
  state: string;
  start: string;
  end: string;
  noSundays: boolean;
  cap: string;
  campaignOnly: boolean;
  notes: string;
}

export const EMPTY_FORM: FormState = {
  state: '',
  start: '',
  end: '',
  noSundays: false,
  cap: '',
  campaignOnly: false,
  notes: '',
};

export function toForm(o: OverrideRecord): FormState {
  return {
    state: o.state ?? '',
    start: o.allowedStart?.slice(0, 5) ?? '',
    end: o.allowedEnd?.slice(0, 5) ?? '',
    noSundays: !o.sundayAllowed,
    cap: o.maxPer24h === null ? '' : String(o.maxPer24h),
    campaignOnly: o.capAppliesTo.length === 1 && o.capAppliesTo[0] === 'campaign',
    notes: o.notes ?? '',
  };
}

export function checkForm(f: FormState): {
  input: OverrideInput | null;
  error: string | null;
  noEffect: boolean;
} {
  const cap = f.cap.trim() === '' ? null : Number(f.cap);
  if (cap !== null && (!Number.isInteger(cap) || cap < 1 || cap > 100)) {
    return {
      input: null,
      error: 'Texts per 24 hours must be a whole number from 1 to 100.',
      noEffect: false,
    };
  }
  if (f.start && f.end && f.start >= f.end) {
    return {
      input: null,
      error: 'The earliest time must be before the latest time.',
      noEffect: false,
    };
  }
  if (!f.start && !f.end && !f.noSundays && cap === null) {
    return { input: null, error: 'Set at least one limit.', noEffect: false };
  }
  // The base windows already run 8:00–21:00 at most: a rule outside them changes nothing.
  const noEffect =
    (!f.start || f.start <= '08:00') &&
    (!f.end || f.end >= '21:00') &&
    !f.noSundays &&
    cap === null;
  return {
    input: {
      state: f.state === '' ? null : f.state,
      allowedStart: f.start || null,
      allowedEnd: f.end || null,
      sundayAllowed: !f.noSundays,
      maxPer24h: cap,
      capAppliesTo: f.campaignOnly ? ['campaign'] : ['campaign', 'reply'],
      notes: f.notes.trim() === '' ? null : f.notes.trim(),
    },
    error: null,
    noEffect,
  };
}
